import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Sparkles, 
  MessageSquare, 
  Globe, 
  Zap, 
  ArrowRight,
  PlaneTakeoff,
  MapPin,
  Calendar,
  Users,
  Brain,
  BookOpen,
  GraduationCap
} from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/context/AuthContext";



const Homepage: React.FC = () => {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  const features = [
    {
      icon: <Brain className="h-8 w-8 text-emerald-600" />,
      title: "AI-Powered Research & Planning",
      description: "Generate comprehensive client proposals with hotels, activities, and flights using advanced AI technology."
    },
    {
      icon: <MessageSquare className="h-8 w-8 text-orange-600" />,
      title: "Intelligent Assistant",
      description: "Chat naturally with AIVIA to research destinations, compare options, and refine client itineraries."
    },
    {
      icon: <Globe className="h-8 w-8 text-emerald-600" />,
      title: "Real-Time Market Data",
      description: "Access current pricing, availability, and market intelligence from trusted industry sources."
    },
    {
      icon: <BookOpen className="h-8 w-8 text-orange-600" />,
      title: "Industry Knowledge Base",
      description: "Connected to comprehensive travel industry datasets, destination guides, and professional insights."
    },
    {
      icon: <GraduationCap className="h-8 w-8 text-emerald-600" />,
      title: "Supplier Intelligence",
      description: "Access verified supplier partnerships, training materials, and commission structures for informed recommendations."
    },

  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-800">
      {/* Hero Section */}
      <div className="container mx-auto px-6 py-20">
        <div className="text-center mb-20">
          {/* Logo */}
          <div className="flex justify-center items-center mb-8">
            <img 
              src="/assets/aivia-logo-blue-transparent-crop.png" 
              alt="AIVIA" 
              className="h-20 w-auto object-contain"
            />
          </div>
          
          {/* Main Headline */}
          <h1 className="text-6xl md:text-7xl font-black tracking-tight mt-[30px] mb-[60px]">
            <span className="bg-gradient-to-r from-orange-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent text-[50px]">The Intelligent Workspace For Travel Advisors</span>
          </h1>
          
          {/* Big Bold Promise - Stamped 5x Design */}
          <div className="relative mb-12 max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-emerald-500/20 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-r from-orange-500/10 to-emerald-500/10 border border-orange-400/30 rounded-3xl p-8 backdrop-blur-md overflow-visible">
              {/* Stamped 5x that breaks out of the container */}
              <div className="absolute -top-8 -right-8 z-10">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-emerald-400 rounded-full blur-2xl opacity-60 scale-110"></div>
                  <div className="relative bg-gradient-to-br from-orange-500 to-amber-500 rounded-full w-48 h-48 flex items-center justify-center shadow-2xl border-4 border-white/20 transform rotate-12">
                    <span className="text-8xl font-black text-white drop-shadow-2xl">5X</span>
                  </div>
                </div>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight pr-20">
                Increase Your Productivity By
              </h2>
              
            </div>
          </div>
          
          {/* Subtitle */}
          <p className="text-2xl text-gray-300 mb-6 max-w-4xl mx-auto font-light leading-relaxed">Get WAY more done in WAY less time.</p>
          
          <p className="text-lg text-gray-400 mb-12 max-w-3xl mx-auto">
            Connected to deep datasets of travel industry knowledge and supplier training. 
            Powered by advanced AI to help travel professionals create exceptional client experiences--FAST!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-emerald-600 text-white px-10 py-5 text-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/25 rounded-2xl"
              onClick={() => setLocation("/waiting-list")}
            >
              Start Your Workspace
              <ArrowRight className="ml-3 h-6 w-6" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              className="border-2 border-slate-600 bg-slate-800/80 text-white hover:bg-slate-700 hover:border-slate-500 px-8 py-5 text-lg font-semibold rounded-2xl backdrop-blur-sm"
              onClick={() => setLocation("/login")}
            >
              Sign In
            </Button>
          </div>
        </div>

        {/* Features Grid */}
        <div className="flex flex-wrap justify-center gap-8 mb-20">
          {features.map((feature, index) => (
            <Card key={index} className="group hover:shadow-2xl hover:shadow-orange-500/10 transition-all duration-500 border-0 bg-slate-800/40 backdrop-blur-md border border-slate-700/50 hover:border-orange-400/50 w-full max-w-sm hover:transform hover:scale-105">
              <CardHeader className="text-center pb-6">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-2xl bg-gradient-to-br from-orange-500/20 to-emerald-500/20 group-hover:from-orange-400/30 group-hover:to-emerald-400/30 transition-all duration-300">
                    {React.cloneElement(feature.icon, { className: "h-8 w-8 text-orange-300 group-hover:text-orange-200 transition-colors duration-300" })}
                  </div>
                </div>
                <CardTitle className="text-xl font-bold text-white group-hover:text-orange-100 transition-colors duration-300">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center px-6 pb-8">
                <p className="text-gray-400 leading-relaxed group-hover:text-gray-300 transition-colors duration-300">
                  {feature.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats Section */}
        <div className="bg-gradient-to-r from-slate-800/60 to-slate-700/60 backdrop-blur-lg rounded-3xl p-12 mb-20 border border-slate-600/40">
          <div className="grid md:grid-cols-4 gap-12 text-center">
            <div className="group">
              <div className="text-5xl font-black text-transparent bg-gradient-to-br from-orange-400 to-emerald-400 bg-clip-text mb-4 group-hover:scale-110 transition-transform duration-300">5+</div>
              <div className="text-gray-300 font-semibold text-lg">AI Providers</div>
            </div>
            <div className="group">
              <div className="text-5xl font-black text-transparent bg-gradient-to-br from-emerald-400 to-cyan-400 bg-clip-text mb-4 group-hover:scale-110 transition-transform duration-300">∞</div>
              <div className="text-gray-300 font-semibold text-lg">Destinations</div>
            </div>
            <div className="group">
              <div className="text-5xl font-black text-transparent bg-gradient-to-br from-cyan-400 to-orange-400 bg-clip-text mb-4 group-hover:scale-110 transition-transform duration-300">24/7</div>
              <div className="text-gray-300 font-semibold text-lg">AI Assistant</div>
            </div>
            <div className="group">
              <div className="text-4xl font-black text-transparent bg-gradient-to-br from-orange-300 to-amber-300 bg-clip-text mb-4 group-hover:scale-110 transition-transform duration-300">Real-time</div>
              <div className="text-gray-300 font-semibold text-lg">Web Search</div>
            </div>
          </div>
        </div>

        {/* How It Works */}
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-white mb-12">
            Your Professional Workflow
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <div className="bg-orange-400/20 border border-orange-400/30 p-4 rounded-full mb-4">
                <MessageSquare className="h-8 w-8 text-orange-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">1. Research & Discover</h3>
              <p className="text-gray-300">
                Chat with AIVIA about client preferences, budget constraints, and travel goals to explore options.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-emerald-400/20 border border-emerald-400/30 p-4 rounded-full mb-4">
                <Brain className="h-8 w-8 text-emerald-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">2. Generate Proposals</h3>
              <p className="text-gray-300">
                AI creates comprehensive client proposals with verified pricing, supplier details, and commission data.
              </p>
            </div>
            
            <div className="flex flex-col items-center">
              <div className="bg-cyan-400/20 border border-cyan-400/30 p-4 rounded-full mb-4">
                <PlaneTakeoff className="h-8 w-8 text-cyan-300" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-white">3. Close & Deliver</h3>
              <p className="text-gray-300">
                Present professional proposals to clients and manage bookings through your existing workflow.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-600/20 to-emerald-600/20 rounded-3xl blur-2xl"></div>
          <div className="relative bg-gradient-to-r from-orange-500/10 to-emerald-500/10 border border-orange-400/30 rounded-3xl p-16 text-center backdrop-blur-md">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white leading-tight">
              Ready to <span className="text-transparent bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text">Transform</span> Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Join travel professionals using AIVIA to deliver exceptional client experiences, 
              cut research time by 80%, and grow their business with AI-powered intelligence.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-emerald-600 text-white px-12 py-6 text-xl font-bold transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/30 rounded-2xl"
              onClick={() => setLocation("/auth")}
            >
              Start Your Workspace Free
              <ArrowRight className="ml-4 h-6 w-6" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Homepage;